import {
    ComputeStatus,
    DataSourceWithConfigAndEtlExtended,
    InputDataSourceConfig,
    TargetStatus,
    UpdateDataSourceConfigMutation,
    useFetchHasPriceRequiringResourcesQuery,
    useUpdateDataSourceConfigMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    Button,
    FlexScrollArea,
    FlexScrollAreaContainer,
    IconAvatar,
    InfoCell,
    Stack,
    Tooltip,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    AcUnitOutline,
    ArrowForwardOutline,
    CheckCircle,
    Info,
} from "@biggeo/bg-ui/lab/icons";
import { isSuccess } from "@vividtheory/remotedata";

import { useCloseModal } from "../../common/redux/hooks";
import { useErrorToast } from "../../toaster/containers/redux/hooks";

export const DisableHotComputeContainer = ({
    datasource,
    onConfirm,
}: {
    datasource: DataSourceWithConfigAndEtlExtended;
    onConfirm: (data: UpdateDataSourceConfigMutation) => void;
}) => {
    const closeModal = useCloseModal();
    const errorToast = useErrorToast();

    const { remote } = useFetchHasPriceRequiringResourcesQuery({
        variables: {
            fkDataSourceId: datasource.dataSource.id,
        },
        onError: () => {
            errorToast(
                "An error occurred while fetching associated Data Products. Please try again later."
            );
        },
    });
    const hasPriceRequiringResources = isSuccess(remote)
        ? remote.data.fetchHasPriceRequiringResources
        : true;

    const {
        executeMutation: updateDataSourceConfig,
        mutationReturn: [_x, { loading: updateLoading }],
    } = useUpdateDataSourceConfigMutation();

    const disableResource = async () => {
        const input: InputDataSourceConfig = {
            id: datasource.configuration.id,
            status: ComputeStatus.unloading,
            targetState: TargetStatus.cold,
        };

        return await updateDataSourceConfig({
            variables: {
                input: input,
            },
        });
    };

    const handleConfirm = async () => {
        const value = await disableResource();
        if (!value.data?.updateDataSourceConfig) {
            errorToast("Failed to disable hot compute.");
            return;
        }
        onConfirm(value.data);
        closeModal();
    };

    return (
        <FlexScrollAreaContainer>
            <FlexScrollArea height="fit-content">
                <Stack width="100%" sx={{ padding: 4 }} gap={4}>
                    <Stack
                        width="100%"
                        gap={1}
                        flexDirection="row"
                        alignItems="center"
                    >
                        <IconAvatar color="success">
                            <CheckCircle color="success" />
                        </IconAvatar>
                        <ArrowForwardOutline size="md" />
                        <IconAvatar color="info">
                            <AcUnitOutline color="info" />
                        </IconAvatar>
                    </Stack>
                    <Stack width="100%">
                        <Typography variant="h6" fontWeight="bold">
                            {"Disable Compute Resources"}
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight="regular"
                            textColor="disabled.onContainer"
                        >
                            {
                                "You will lose instant sync and usage to this dataset "
                            }
                        </Typography>
                    </Stack>
                    {hasPriceRequiringResources && (
                        <InfoCell
                            startNode={<Info size="sm" color="info" />}
                            title={
                                <Typography
                                    variant="body2"
                                    fontWeight="semibold"
                                >
                                    Cannot disable resources.
                                </Typography>
                            }
                            subtitle={
                                <Typography variant="body4">
                                    This Data Source has Data Products with
                                    'Always On' usage and Datascape access.
                                    These Data Products must be updated or
                                    removed before disabling resources.
                                </Typography>
                            }
                            color="info"
                            sx={{
                                backgroundColor: (theme) =>
                                    theme.palette.info.container,
                            }}
                        />
                    )}
                </Stack>
            </FlexScrollArea>
            <Stack width="100%" sx={{ padding: 4 }} gap={4}>
                <Tooltip
                    title="This Data Source has Data Products with 'Always On' usage and Datascape access. These Data Products must be updated or removed before disabling resources."
                    disabled={!hasPriceRequiringResources}
                >
                    <span>
                        <Button
                            onClick={handleConfirm}
                            fullWidth
                            disabled={
                                updateLoading || hasPriceRequiringResources
                            }
                        >
                            Disable Resources
                        </Button>
                    </span>
                </Tooltip>
                <Button fullWidth variant="outlined" onClick={closeModal}>
                    Nevermind
                </Button>
            </Stack>
        </FlexScrollAreaContainer>
    );
};
